/* DreamCloud → Replay conversions: re-send sales Google Ads never got, after an
 * outage for instance.
 *
 *   pick dates → List sales (sales_upload_status) → untick / tick → Dry run
 *   (replay, dry_run: true) → Send N conversions (replay, dry_run: false, in
 *   batches of 200) → the list again, with the statuses it now has.
 *
 * The flow is replayStep in js/rules/dreamcloud.js. What matters most lives
 * there: the send carries the ids and destinations the dry run was run for
 * (previewIds / previewDestinations), and changing a tick or a destination
 * after the dry run needs a new one. Pre-ticked are only the sales that
 * evidently did not land (defaultTicks); an accepted sale is never pre-ticked,
 * and after a send whose result is unknown nothing is (unknownSend).
 * Only the Google Ads sale is sent unless GA4 or the Appointment Booked
 * restatement is ticked here.
 */
import * as Vue from 'vue';
import { dreamcloud } from '../dreamcloud.js';
import {
  initialReplay, replayStep, replayBatches, replayCount, mergePreviews, previewLabel, rangeError,
  yesterday, money, statusLabel, formatWhen, DESTINATIONS, DEST_LABELS,
  replaySentText, replayFailureText, errorText
} from '../rules/dreamcloud.js';

// One line on each destination's risk, from the design (Kevin's call).
var RISK = {
  google_ads: 'Always sent. Google counts a re-sent sale as an adjustment, not a second conversion.',
  ga4: 'GA4 may not de-duplicate a re-sent purchase, so this can double revenue.',
  google_ads_booking: 'Fails on clicks that only carry a gbraid.'
};

function said(err) { return errorText(err); }

export const DcReplay = {
  name: 'DcReplay',
  props: {
    start: { type: String, default: null },
    end: { type: String, default: null }
  },
  emits: ['close', 'busy'],
  setup: function (props, ctx) {
    var first = props.start || yesterday();
    var s = Vue.ref(initialReplay(first, props.end || first));
    var busy = Vue.ref(null);        // 'list' | 'preview' | 'send' | null
    var outcome = Vue.ref(null);     // { ok, text } after a send, until the next dry run

    function go(e) { s.value = replayStep(s.value, e); }

    var dates = Vue.reactive({ start: s.value.start, end: s.value.end });
    function setDates() { go({ type: 'range', start: dates.start, end: dates.end }); }

    var formError = Vue.computed(function () { return rangeError(dates.start, dates.end); });

    /* The latest list request wins: a slow answer for dates since changed is
     * not written over the new ones. */
    var listed = 0;
    async function list() {
      if (formError.value || busy.value) return;
      var mine = ++listed;
      var range = { start: s.value.start, end: s.value.end };
      busy.value = 'list';
      try {
        var rows = await dreamcloud.invoke('sales_upload_status', range);
        if (mine !== listed || s.value.start !== range.start || s.value.end !== range.end) return;
        go({ type: 'listed', rows: Array.isArray(rows) ? rows : [] });
      } catch (err) {
        if (mine === listed) go({ type: 'error', error: said(err) });
      } finally {
        if (mine === listed) busy.value = null;
      }
    }

    function tick(id, on) { go({ type: 'tick', id: id, on: on }); }
    function dest(id, on) { go({ type: 'destination', id: id, on: on }); }

    /* The dry run, for exactly the ticks and destinations as they are now. If
     * either changes while it is out, replayStep drops the answer. */
    async function preview() {
      if (busy.value || s.value.step !== 'listed' || !s.value.ticks.length) return;
      var ids = s.value.ticks.slice();
      var destinations = s.value.destinations.slice();
      busy.value = 'preview';
      outcome.value = null;
      try {
        var answers = [];
        var batches = replayBatches(ids);
        for (var i = 0; i < batches.length; i++) {
          answers.push(await dreamcloud.invoke('replay', { sale_ids: batches[i], dry_run: true, destinations: destinations }));
        }
        go({ type: 'previewed', ids: ids, destinations: destinations, response: mergePreviews(answers) });
      } catch (err) {
        go({ type: 'error', error: said(err) });
      } finally {
        busy.value = null;
      }
    }

    /* The send: what the dry run looked at, batch by batch. A batch whose send
     * to Google failed stops the rest (the worker answers 200 with
     * ingest.status 'error' for that). Then the list again, so the statuses
     * show what landed. */
    async function send() {
      if (busy.value) return;
      go({ type: 'confirm' });
      if (s.value.step !== 'sending') return;
      var plan = s.value.sending;
      busy.value = 'send';
      // The parent holds the dialog open, and the page keeps it mounted, until
      // this ends (dc-actions.js, dreamcloud.js). The outcome goes to the store
      // too, for an owner who switched apps mid-send.
      ctx.emit('busy', true);
      dreamcloud.beginWrite();
      var answers = [];
      var result = null;
      try {
        for (var i = 0; i < plan.batches.length; i++) {
          var r;
          try {
            r = await dreamcloud.invoke('replay', { sale_ids: plan.batches[i], dry_run: false, destinations: plan.destinations });
          } catch (err) {
            // Network, timeout or a 5xx: the worker may have sent this batch.
            throw { batch: i + 1, unknown: true, error: errorText(err) };
          }
          if (r && r.ingest && r.ingest.status === 'error') {
            // The worker's own word that the Google call failed: nothing sent.
            throw { batch: i + 1, unknown: false, error: r.ingest.message || 'no reason given' };
          }
          answers.push(r);
        }
        go({ type: 'sent', responses: answers });
        result = { ok: true, text: replaySentText(s.value.sent) + ' The statuses below are re-read.' };
      } catch (f) {
        var stop = f && f.batch ? f : { batch: answers.length + 1, unknown: true, error: errorText(f) };
        go({ type: 'failed', error: stop.error, responses: answers, unknown: stop.unknown !== false });
        result = { ok: false, text: replayFailureText({
          batch: stop.batch, batches: plan.batches.length, error: stop.error, unknown: stop.unknown !== false, sent: s.value.sent
        }) };
      } finally {
        busy.value = null;
        outcome.value = result;
        dreamcloud.endWrite(result ? { ok: result.ok, text: 'Replay: ' + result.text.replace(' The statuses below are re-read.', '').replace('the statuses below', 'the sales’ statuses') } : null);
        ctx.emit('busy', false);
      }
      dreamcloud.load();
      list();
    }

    var ticked = Vue.computed(function () { return new Set(s.value.ticks); });
    var byId = Vue.computed(function () {
      var m = new Map();
      var res = s.value.preview && s.value.preview.results;
      (res || []).forEach(function (r) { m.set(r.sale_id, r); });
      return m;
    });
    var count = Vue.computed(function () { return replayCount(s.value); });
    var skipped = Vue.computed(function () {
      var res = (s.value.preview && s.value.preview.results) || [];
      return res.filter(function (r) { return r.status !== 'would_send'; }).length;
    });
    var tickedAccepted = Vue.computed(function () {
      return s.value.rows.filter(function (r) { return r.status === 'accepted' && ticked.value.has(r.sale_id); }).length;
    });
    var tickable = Vue.computed(function () {
      return !busy.value && (s.value.step === 'listed' || s.value.step === 'previewed');
    });

    function close() { if (busy.value !== 'send') ctx.emit('close'); }

    return {
      s: s, busy: busy, outcome: outcome, dates: dates, formError: formError, setDates: setDates,
      list: list, tick: tick, dest: dest, preview: preview, send: send, close: close,
      ticked: ticked, byId: byId, count: count, skipped: skipped, tickedAccepted: tickedAccepted, tickable: tickable,
      DESTINATIONS: DESTINATIONS, DEST_LABELS: DEST_LABELS, RISK: RISK,
      statusLabel: statusLabel, previewLabel: previewLabel, money: money, when: formatWhen
    };
  },
  template: [
    '<div class="modal-scrim" @click.self="close">',
    '  <div class="sheet dc-sheet" role="dialog" aria-modal="true" aria-labelledby="dc-replay-h">',
    '    <div class="dc-sheet__head">',
    '      <h3 id="dc-replay-h">Replay conversions</h3>',
    '      <button type="button" class="dc-x" aria-label="Close" @click="close" :disabled="busy === \'send\'">&times;</button>',
    '    </div>',
    '    <div class="dc-sheet__body">',
    '',
    '      <form class="dc-form" @submit.prevent="list">',
    '        <label>From <input type="date" v-model="dates.start" @change="setDates" :disabled="!!busy" /></label>',
    '        <label>To <input type="date" v-model="dates.end" @change="setDates" :disabled="!!busy" /></label>',
    '        <button type="submit" class="btn btn--sm dc-btn" :disabled="!!formError || !!busy">',
    '          {{ busy === \'list\' ? \'Listing…\' : \'List sales\' }}</button>',
    '      </form>',
    '      <p v-if="formError && dates.start && dates.end" class="dc-error">{{ formError }}</p>',
    '',
    '      <fieldset class="dc-dests">',
    '        <legend>Send to</legend>',
    '        <label v-for="d in DESTINATIONS" :key="d" class="dc-dest">',
    '          <input type="checkbox" :aria-label="DEST_LABELS[d]" :checked="s.destinations.indexOf(d) >= 0"',
    '            :disabled="d === \'google_ads\' || !!busy"',
    '            @change="dest(d, $event.target.checked)" />',
    '          <span><strong>{{ DEST_LABELS[d] }}</strong> <span class="dc-muted">{{ RISK[d] }}</span></span>',
    '        </label>',
    '      </fieldset>',
    '',
    '      <p v-if="outcome" :class="outcome.ok ? \'dc-ok\' : \'dc-error\'" role="status">{{ outcome.text }}</p>',
    '      <p v-if="s.error" class="dc-error" role="alert">{{ s.error }}</p>',
    '',
    '      <template v-if="s.step !== \'pick\'">',
    '        <p v-if="!s.rows.length" class="dc-muted">No sales closed in those dates.</p>',
    '        <div v-else class="dc-rows dc-replay__rows">',
    '          <table class="dc-table">',
    '            <thead><tr><th><span class="dc-sr">Send</span></th><th>Sale</th><th>Closed</th><th>Total</th>',
    '              <th>Click</th><th>Status</th><th v-if="s.step === \'previewed\'">Dry run</th></tr></thead>',
    '            <tbody><tr v-for="r in s.rows" :key="r.sale_id" :class="{ \'is-accepted\': r.status === \'accepted\' }">',
    '              <td><input type="checkbox" :aria-label="\'Send sale \' + r.sale_id"',
    '                :checked="ticked.has(r.sale_id)" :disabled="!tickable"',
    '                @change="tick(r.sale_id, $event.target.checked)" /></td>',
    '              <td>#{{ r.sale_id }}</td><td>{{ when(r.closed_at) }}</td><td>{{ money(r.total) }}</td>',
    '              <td>{{ r.has_click ? \'Yes\' : \'No\' }}</td>',
    '              <td class="dc-table__wrap"><span class="dc-status" :class="\'is-\' + r.status">{{ statusLabel(r.status) }}</span>',
    '                <span v-if="!r.eligible" class="dc-muted"> · not eligible<span v-if="r.ineligible_reason"> ({{ r.ineligible_reason }})</span></span></td>',
    '              <td v-if="s.step === \'previewed\'" class="dc-table__wrap">',
    '                {{ byId.has(r.sale_id) ? previewLabel(byId.get(r.sale_id)) : \'\' }}</td>',
    '            </tr></tbody>',
    '          </table>',
    '        </div>',
    '',
    '        <p v-if="tickedAccepted" class="dc-warn">{{ tickedAccepted }} ticked',
    '          {{ tickedAccepted === 1 ? \'sale was\' : \'sales were\' }} already accepted by Google.</p>',
    '        <p v-if="s.unknownSend && s.rows.length" class="dc-muted">Nothing is pre-ticked: the last send&rsquo;s',
    '          result is unknown, so tick only the sales you mean to send again.</p>',
    '',
    '        <div v-if="s.step === \'previewed\'" class="dc-totals" role="status">',
    '          <strong>Dry run:</strong>',
    '          <span v-for="d in s.previewDestinations" :key="d">{{ s.preview.would_send[d] || 0 }} {{ DEST_LABELS[d] }}</span>',
    '          <span v-if="skipped">{{ skipped }} skipped</span>',
    '          <span class="dc-muted">Nothing has been sent.</span>',
    '        </div>',
    '      </template>',
    '    </div>',
    '',
    '    <div class="dc-sheet__foot">',
    '      <span class="dc-muted">{{ s.ticks.length }} ticked</span>',
    '      <button v-if="s.step !== \'previewed\' && s.step !== \'sending\'" type="button" class="btn btn--primary"',
    '        :disabled="!!busy || s.step !== \'listed\' || !s.ticks.length" @click="preview">',
    '        {{ busy === \'preview\' ? \'Running the dry run…\' : \'Dry run\' }}</button>',
    '      <button v-else type="button" class="btn btn--danger" :disabled="!!busy || !count" @click="send">',
    '        {{ busy === \'send\' ? \'Sending…\' : count ? \'Send \' + count + \' conversion\' + (count === 1 ? \'\' : \'s\') : \'Nothing to send\' }}</button>',
    '      <button type="button" class="btn btn--quiet" @click="close" :disabled="busy === \'send\'">Close</button>',
    '    </div>',
    '  </div>',
    '</div>'
  ].join('\n')
};
